import { Api } from "@Shared/schemas";
import { useApiMutation } from "@hooks";
import { AppContext, Events, UnityBridge } from "@Context/common/general/context";
import GameModes from "@Enums/GameModes";
import { AnalyticsEvents, PlaySessionTypes } from "@Enums/index";
import PlayPositions from "@Enums/PlayPositions";
import PlayPositionsShortnameMap from "@Enums/PlayPositionsShortnameMap";
import VRHeaderButtonType from "@Enums/VRHeaderButtonType";
import { GameplayAction, GameplayEvent, GameplayModule } from "@Globals/types";
import {
  getMatchPlayBackendPosition,
  getMatchPlayDifficultyKey,
  getMatchPlaySpeedKey,
  getRandomMatchPlayPosition,
  loadMatchPlaySettings,
  matchPlayDefaults,
  matchPlayPositions,
  matchPlaySettingValues,
  saveMatchPlaySettings,
} from "@Helpers/match-play";
import useAnalytics from "@hooks/common/analytics/use-analytics";
import useCompletePlaySession from "@hooks/game/use-complete-play-session";
import { useMatchPlayPositionSelection } from "@hooks/game/use-position-selection";
import useI18n from "@i18n";
import { compareVersions } from "compare-versions";
import { first, isNumber } from "lodash-es";
import { useContext, useEffect, useState } from "react";

export const useMatchPlay = () => {
  const { state } = useContext(AppContext);
  const createPlaySession = useApiMutation({ api: Api.Sessions.CreatePlaySession });
  const { i18n } = useI18n();
  const { track } = useAnalytics();

  const { selectedPositions, setSelectedPositions } = useMatchPlayPositionSelection();
  const [matchPlaySettings] = useState(loadMatchPlaySettings);
  const [difficulty, setDifficulty] = useState(matchPlaySettings.difficulty);
  const [gameSpeed, setGameSpeed] = useState(matchPlaySettings.gameSpeed);
  const [gameDuration, setGameDuration] = useState<number>(matchPlaySettings.gameDuration);
  const [isWaitingForGameplay, setIsWaitingForGameplay] = useState(false);
  const [isPositionSelectOpen, setIsPositionSelectOpen] = useState(false);

  useEffect(() => {
    saveMatchPlaySettings({ difficulty, gameSpeed, gameDuration });
  }, [difficulty, gameSpeed, gameDuration]);

  const normalizedSelectedPositions = selectedPositions.filter(position => matchPlayPositions.includes(position)).slice(0, 1);
  const initialSelectedPositions = normalizedSelectedPositions;

  const handleGameSpeedChange = (_event: Event, value: number | number[]) => {
    const nextValue = value as number;
    if (!isNumber(nextValue)) {
      return;
    }

    const clampedSpeed = Math.max(matchPlaySettingValues.speed.slow, Math.min(nextValue, matchPlaySettingValues.speed.fast));
    const nextSpeed = getMatchPlaySpeedKey(clampedSpeed);
    setGameSpeed(nextSpeed);
    track(AnalyticsEvents.matchplay_settings_adjusted, { game_speed: nextSpeed });
  };

  const handleDifficultyChange = (_event: Event, value: number) => {
    if (!isNumber(value)) {
      return;
    }

    const clampedDifficulty = Math.max(matchPlaySettingValues.difficulty.easy, Math.min(value, matchPlaySettingValues.difficulty.hard));
    const nextDifficulty = getMatchPlayDifficultyKey(clampedDifficulty);
    setDifficulty(nextDifficulty);
    track(AnalyticsEvents.matchplay_settings_adjusted, { difficulty: nextDifficulty });
  };

  const handleGameDurationChange = (_event: Event, value: number) => {
    if (!isNumber(value)) {
      return;
    }

    setGameDuration(Math.max(matchPlayDefaults.duration.min, Math.min(value, matchPlayDefaults.duration.max)));
    track(AnalyticsEvents.matchplay_settings_adjusted, { game_duration: value });
  };

  const handlePositionsSelected = (positions: PlayPositions[]) => {
    setSelectedPositions(positions);

    const nextSelectedPlayPosition = positions[0];
    if (!nextSelectedPlayPosition) {
      setIsPositionSelectOpen(false);
      return;
    }

    track(AnalyticsEvents.matchplay_position_updated, {
      position: PlayPositionsShortnameMap[nextSelectedPlayPosition],
      position_name: nextSelectedPlayPosition,
    });
    setIsPositionSelectOpen(false);
  };

  // @davidrocha: Legacy code - Remove once we support Matchplay with Matchcore
  const getLegacyMatchPlayPositions = (position: PlayPositions): number => {
    switch (position) {
      case PlayPositions.GOALKEEPER:
        return 0;
      case PlayPositions.LEFT_FULLBACK:
        return 4;
      case PlayPositions.LEFT_CENTER_BACK:
        return 3;
      case PlayPositions.RIGHT_CENTER_BACK:
        return 2;
      case PlayPositions.RIGHT_FULLBACK:
        return 1;
      case PlayPositions.DEFENSIVE_MIDFIELDER:
        return 5;
      case PlayPositions.CENTER_MIDFIELDER:
        return 7;
      case PlayPositions.ATTACKING_MIDFIELDER:
        return 9;
      case PlayPositions.STRIKER:
        return 8;
      case PlayPositions.LEFT_WINGER:
        return 10;
      case PlayPositions.RIGHT_WINGER:
        return 6;
      default:
        return 10;
    }
  };

  const startMatch = (playSessionId: number, cognitiveResultId: number, playPosition: PlayPositions) => {
    // @davidrocha: getLegacyMatchPositions for current prod matchplay and PlayPositionsShortnameMap for Matchcore
    const payload = {
      GFMessage: "quickPlay",
      data: {
        playSessionId,
        cognitiveResultId,
        playerPosition:
          compareVersions(state.app.clientVersion, "3.0.0") === -1
            ? getLegacyMatchPlayPositions(playPosition)
            : PlayPositionsShortnameMap[playPosition],
        difficulty,
        speed: gameSpeed,
        duration: gameDuration * 60,
      },
    };

    setIsWaitingForGameplay(true);

    UnityBridge.dispatchWebEvent({
      type: UnityBridge.OutgoingWebEvents.GFootball,
      payload,
    });
  };

  const startQuickPlay = () => {
    if (createPlaySession.isPending || isWaitingForGameplay) return;
    const playPosition = normalizedSelectedPositions[0] ?? getRandomMatchPlayPosition();
    const backendPlayPosition = getMatchPlayBackendPosition(playPosition);

    track(AnalyticsEvents.matchplay_started, {
      playerPosition: PlayPositionsShortnameMap[playPosition],
      difficulty,
      game_speed: gameSpeed,
      game_duration: gameDuration,
    });

    createPlaySession.mutate(
      {
        account_id: state.account?.id ?? null,
        session_type: PlaySessionTypes.PRACTISE_SESSION,
        game_mode: GameModes.MATCH_PLAY,
        play_position: backendPlayPosition,
      },
      {
        onSuccess: data => {
          const playItem = first(data.play_items);
          startMatch(data.play_session_id, playItem.cognitive_result_id, playPosition);
        },
      }
    );
  };

  const showMatchPlayResults = (resultsData: { play_session_id: number }) => {
    state.app.events.navigate.emit({
      path: `/home/matchplay/results/${resultsData.play_session_id}`,
      data: resultsData,
    });
  };

  const { isCompletingSession } = useCompletePlaySession({
    onSuccess: (_, playSessionId) => showMatchPlayResults({ play_session_id: playSessionId }),
  });

  const onGameplayEntered = (event: GameplayEvent) => {
    if (event.action === GameplayAction.ENTER && event.module === GameplayModule.MATCHPLAY) {
      setIsWaitingForGameplay(false);
    }
  };

  useEffect(() => {
    if (selectedPositions.length > 1) {
      setSelectedPositions([selectedPositions[0]]);
    }
  }, [selectedPositions, setSelectedPositions]);

  useEffect(() => {
    state.app.events.gameplayEntered.uniq(onGameplayEntered);
    Events.app.showHeaderButton.emit({
      type: VRHeaderButtonType.BACK,
      title: i18n("match-play.header-title"),
      badge: i18n("common.beta"),
    });

    return () => {
      state.app.events.gameplayEntered.remove(onGameplayEntered);
      Events.app.hideHeaderButton.emit();
    };
  }, []);

  return {
    difficulty,
    gameDuration,
    gameSpeed,
    handleDifficultyChange,
    handleGameDurationChange,
    handleGameSpeedChange,
    handlePositionsSelected,
    initialSelectedPositions,
    isPositionSelectOpen,
    isWaitingForGameplay: isWaitingForGameplay || createPlaySession.isPending,
    isCompletingSession,
    openPositionSelect: () => !isWaitingForGameplay && setIsPositionSelectOpen(true),
    closePositionSelect: () => setIsPositionSelectOpen(false),
    selectedPositions: normalizedSelectedPositions,
    startQuickPlay,
  };
};

export default useMatchPlay;
